import { Input, OnChanges, OnDestroy, SimpleChanges, Directive, OnInit } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, tap, catchError } from 'rxjs/operators';
import { TeamMetricResult } from 'src/app/models/team-metrics';
import { TeamMetricsService } from 'src/app/services/team-metrics.service';

@Directive()
export abstract class BaseMetricComponent<T extends TeamMetricResult> implements OnInit, OnChanges, OnDestroy {
  @Input() team: string | null = null;
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() org: string | null = null;
  @Input() project: string | null = null;
  @Input() isOtherMetricLoading: boolean | null = false;

  metricDataToDisplay: T | null = null;
  isLoading: boolean = false;
  showSpinner: boolean = false;
  
  protected retrievedData: T | null = null;
  protected readonly destroy$ = new Subject<void>();
  protected previousInputs: { team: string, startDate: string, endDate: string } = { team: '', startDate: '', endDate: '' };

  constructor(protected teamMetricsService: TeamMetricsService) {}

  ngOnInit(): void {
    // Base implementation, can be overridden by subclasses
  }

  protected abstract callFetchMetric(
    team: string, 
    isoStartDate: string, 
    isoEndDate: string,
    org: string,
    project: string
  ): Observable<T | null>;

  ngOnChanges(changes: SimpleChanges): void {
    const teamChanged = changes['team'];
    const startDateChanged = changes['startDate'];
    const endDateChanged = changes['endDate'];
    const isOtherMetricLoading = changes['isOtherMetricLoading'];

    const currentTeam = this.team ?? '';
    const currentStartDate = this.startDate ? this.startDate.toISOString() : '';
    const currentEndDate = this.endDate ? this.endDate.toISOString() : '';

    const inputsHaveChanged = 
      (teamChanged && currentTeam !== this.previousInputs.team) ||
      (startDateChanged && currentStartDate !== this.previousInputs.startDate) ||
      (endDateChanged && currentEndDate !== this.previousInputs.endDate);

    if (inputsHaveChanged) {
      this.previousInputs = { team: currentTeam, startDate: currentStartDate, endDate: currentEndDate };
      this.showSpinner = true;
      this.fetchMetric();
    } else if (!this.retrievedData && !this.isLoading && this.team && this.startDate && this.endDate && !(this.isOtherMetricLoading ?? false) ) {
      this.fetchMetric();
    }

    if (isOtherMetricLoading && !isOtherMetricLoading.firstChange) {
      this.trySetCardData();
    }
  }

  fetchMetric(): void {
    if (this.team && this.startDate && this.endDate && this.org && this.project) {
      this.isLoading = true;

      const isoStartDate = this.startDate.toISOString();
      const isoEndDate = this.endDate.toISOString();

      this.callFetchMetric(this.team, isoStartDate, isoEndDate, this.org, this.project)
        .pipe(
          takeUntil(this.destroy$),
          tap(data => {
            this.retrievedData = data;
            this.isLoading = false;
            this.showSpinner = false;
            this.trySetCardData();
          }),
          catchError(error => {
            console.error(`Error fetching metric in ${this.constructor.name}:`, error);
            this.retrievedData = null;
            this.isLoading = false;
            this.showSpinner = false;
            this.trySetCardData(); 
            return of(null);
          })
        )
        .subscribe();
    } else {
      this.retrievedData = null;
      this.isLoading = false;
      this.showSpinner = false;
      this.trySetCardData();
    }
  }

  protected trySetCardData(): void {
    if (this.retrievedData && !(this.isOtherMetricLoading ?? false)) {
      this.metricDataToDisplay = this.retrievedData;
    } else if (!this.retrievedData && !this.isLoading) {
      this.metricDataToDisplay = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
